/**
 * MarkAsSubmittedDialog Component
 *
 * Dialog for marking a permit application as submitted to the jurisdiction.
 * Captures submission details including method, confirmation number, and documents.
 *
 * Features:
 * - Submission method selection (online, email, in-person, mail)
 * - Confirmation number capture
 * - Document checklist of what was submitted
 * - Notes field for additional details
 * - Audit trail integration
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Globe,
  Mail,
  Building,
  Package,
  CheckCircle2,
  FileText,
  Send,
  Loader2,
  AlertCircle,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuditTrail } from '@/hooks/useAuditTrail';
import type { SubmissionMethod } from '@/types/jurisdiction';

interface SubmittedDocument {
  id: string;
  name: string;
  type: string;
  isRequired?: boolean;
}

interface MarkAsSubmittedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jurisdictionName: string;
  documents?: SubmittedDocument[];
  onSubmit: (data: SubmissionData) => Promise<void>;
}

export interface SubmissionData {
  method: SubmissionMethod;
  confirmationNumber?: string;
  trackingNumber?: string;
  submittedAt: string;
  submittedDocumentIds: string[];
  notes?: string;
}

const submissionMethods: {
  value: SubmissionMethod;
  label: string;
  icon: React.ElementType;
  description: string;
}[] = [
  {
    value: 'online_portal',
    label: 'Online Portal',
    icon: Globe,
    description: 'Submitted through jurisdiction\'s online permit system',
  },
  {
    value: 'email',
    label: 'Email',
    icon: Mail,
    description: 'Sent via email to permit department',
  },
  {
    value: 'in_person',
    label: 'In Person',
    icon: Building,
    description: 'Delivered at permit office in person',
  },
  {
    value: 'mail',
    label: 'Mail',
    icon: Package,
    description: 'Sent via postal mail or courier',
  },
];

export function MarkAsSubmittedDialog({
  open,
  onOpenChange,
  jobId,
  jurisdictionName,
  documents = [],
  onSubmit,
}: MarkAsSubmittedDialogProps) {
  const [method, setMethod] = useState<SubmissionMethod>('online_portal');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { addSubmissionEvent } = useAuditTrail({ jobId });

  // Pre-select all required documents
  useEffect(() => {
    if (open) {
      const requiredIds = documents.filter((d) => d.isRequired).map((d) => d.id);
      setSelectedDocIds(requiredIds.length > 0 ? requiredIds : documents.map((d) => d.id));
    }
  }, [open, documents]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setMethod('online_portal');
      setConfirmationNumber('');
      setTrackingNumber('');
      setNotes('');
      setError(null);
    }
  }, [open]);

  const toggleDocument = (docId: string) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const handleSubmit = async () => {
    if (selectedDocIds.length === 0) {
      setError('Please select at least one document that was submitted.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const submissionData: SubmissionData = {
        method,
        confirmationNumber: confirmationNumber || undefined,
        trackingNumber: trackingNumber || undefined,
        submittedAt: new Date().toISOString(),
        submittedDocumentIds: selectedDocIds,
        notes: notes || undefined,
      };

      // Add to audit trail
      const selectedDocNames = documents
        .filter((d) => selectedDocIds.includes(d.id))
        .map((d) => d.name);

      await addSubmissionEvent({
        method,
        jurisdictionName,
        confirmationNumber: confirmationNumber || undefined,
        submittedDocuments: selectedDocNames,
        status: 'submitted',
      });

      // Call parent handler
      await onSubmit(submissionData);

      onOpenChange(false);
    } catch (err) {
      console.error('Error marking as submitted:', err);
      setError('Failed to record submission. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedMethod = submissionMethods.find((m) => m.value === method);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Mark as Submitted
          </DialogTitle>
          <DialogDescription>
            Record the permit application submission to {jurisdictionName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Submission Method */}
          <div className="space-y-2">
            <Label>Submission Method</Label>
            <Select value={method} onValueChange={(v) => setMethod(v as SubmissionMethod)}>
              <SelectTrigger>
                <SelectValue placeholder="Select how you submitted" />
              </SelectTrigger>
              <SelectContent>
                {submissionMethods.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <div className="flex items-center gap-2">
                      <m.icon className="h-4 w-4" />
                      <span>{m.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMethod && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                {selectedMethod.description}
              </p>
            )}
          </div>

          {/* Confirmation / Tracking Number */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="confirmationNumber">
                Confirmation Number
                <span className="text-muted-foreground font-normal ml-1">(optional)</span>
              </Label>
              <Input
                id="confirmationNumber"
                placeholder="e.g., PRE-2024-123456"
                value={confirmationNumber}
                onChange={(e) => setConfirmationNumber(e.target.value)}
              />
            </div>
            {method === 'mail' && (
              <div className="space-y-2">
                <Label htmlFor="trackingNumber">
                  Tracking Number
                  <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                </Label>
                <Input
                  id="trackingNumber"
                  placeholder="e.g., USPS, FedEx"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Documents Submitted */}
          {documents.length > 0 && (
            <div className="space-y-2">
              <Label>Documents Submitted</Label>
              <ScrollArea className="h-[160px] rounded-md border p-3">
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className={cn(
                        'flex items-center gap-3 p-2 rounded-md transition-colors',
                        selectedDocIds.includes(doc.id)
                          ? 'bg-primary/5 border border-primary/20'
                          : 'hover:bg-muted/50'
                      )}
                    >
                      <Checkbox
                        id={`doc-${doc.id}`}
                        checked={selectedDocIds.includes(doc.id)}
                        onCheckedChange={() => toggleDocument(doc.id)}
                      />
                      <label
                        htmlFor={`doc-${doc.id}`}
                        className="flex-1 flex items-center gap-2 cursor-pointer"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{doc.name}</span>
                        {doc.isRequired && (
                          <Badge variant="secondary" className="text-xs">
                            Required
                          </Badge>
                        )}
                      </label>
                      {selectedDocIds.includes(doc.id) && (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-muted-foreground">
                {selectedDocIds.length} of {documents.length} documents selected
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes
              <span className="text-muted-foreground font-normal ml-1">(optional)</span>
            </Label>
            <Textarea
              id="notes"
              placeholder="Any additional details about the submission..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive p-3 bg-destructive/10 rounded-md">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark as Submitted
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MarkAsSubmittedDialog;
